import moment from "moment";
import type { BandUnit, SelectedBookData, SidebarSelectedWordsList, UserSubject } from "p6m-p6u";
import { MAX_NAME_LENGTH } from "../constants/LimitConstants";
import { composeBookName } from "./TestPreparationHelper";

const MIN_BOOK_NAME_BUDGET = 20;

export type TFunction = (key: string, options?: Record<string, unknown>) => string;

/**
 * Formats a date for the vocab-training title (e.g. "08.05.2026").
 * Uses leading zeros on day/month for a consistent two-digit format.
 */
export const formatTitleDate = (timestamp?: number): string => {
    return moment(timestamp).format("DD.MM.YYYY");
};

const _shortenWithEllipsis = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) {
        return text;
    }

    const cut = text.slice(0, Math.max(maxLength - 1, 0));
    const lastSpace = cut.lastIndexOf(" ");
    // Skip the word boundary when a space-free tail would collapse the title to its first few words.
    const keepsEnoughText = lastSpace > cut.length * 0.6;
    const wordBoundaryCut = keepsEnoughText ? cut.slice(0, lastSpace) : cut;
    return wordBoundaryCut.replace(/[\s,]+$/, "") + "…";
};

const _composeUnitsTitle = (unitNames: string[], t: TFunction): string => {
    const fullTitle = unitNames.join(", ");
    if (fullTitle.length <= MAX_NAME_LENGTH) {
        return fullTitle;
    }

    // Keep dropping units until it fits, re-translating per candidate: the suffix length changes with count and language.
    for (let visibleCount = unitNames.length - 1; visibleCount >= 1; visibleCount--) {
        const candidate = t("{units} + {count} more", {
            _tags: "CreateVocabTraining,VocabTrainingTitleBar",
            units: unitNames.slice(0, visibleCount).join(", "),
            count: unitNames.length - visibleCount,
        });
        if (candidate.length <= MAX_NAME_LENGTH) {
            return candidate;
        }
    }

    // Even one unit plus suffix overshoots: hard-cut instead, "+ N more" on a mangled name would fake precision.
    return _shortenWithEllipsis(fullTitle, MAX_NAME_LENGTH);
};

/**
 * The "Based on the book" title, capped at MAX_NAME_LENGTH: drops the publisher first (least
 * identifying part), keeps the band (identifies the volume) unless the band itself is oversized.
 * The training title may be shortened — Amplitude and test creation use composeBookName directly.
 */
export const getLimitedBookTitle = (selectedBookData: SelectedBookData): string => {
    const fullBookName = composeBookName(selectedBookData);
    if (fullBookName.length <= MAX_NAME_LENGTH) {
        return fullBookName;
    }

    const bookName = selectedBookData.publisherBook?.BookName ?? "";
    const bandSuffix = ` (${selectedBookData.band?.ShortName ?? ""})`;
    const bookNameBudget = MAX_NAME_LENGTH - bandSuffix.length;
    if (bookNameBudget < MIN_BOOK_NAME_BUDGET) {
        return _shortenWithEllipsis(bookName + bandSuffix, MAX_NAME_LENGTH);
    }
    return _shortenWithEllipsis(bookName, bookNameBudget) + bandSuffix;
};

export interface SelectedUnitsTitleParams {
    selectedBandUnits: BandUnit[];
    vocabularyDrawerContent: SidebarSelectedWordsList;
    selectedUserSubject: UserSubject;
    t: TFunction;
}

/**
 * Builds the "Based on vocabulary" title from the names of the units that have at least one
 * selected card (e.g. "At the sea, p. 21, p. 22, …"), preserving the unit order, capped at
 * MAX_NAME_LENGTH. For self-created content (no band units) it falls back to the subject name.
 */
export const getSelectedUnitsTitle = (params: SelectedUnitsTitleParams): string => {
    const selectedUnitIds = new Set<string>();
    Object.values(params.vocabularyDrawerContent).forEach((entry) => {
        const content = entry.wordContent;
        if (content && "unitId" in content && content.unitId) {
            selectedUnitIds.add(String(content.unitId));
        }
    });

    const unitNames = params.selectedBandUnits
        .filter((unit) => unit.id && selectedUnitIds.has(String(unit.id)))
        .map((unit) => unit.name)
        .filter((name): name is string => Boolean(name));

    if (unitNames.length > 0) {
        return _composeUnitsTitle(unitNames, params.t);
    }

    // Own content has no band units, so fall back to the subject name — but only while vocab is
    // actually selected. Otherwise the title would keep showing the subject after the teacher
    // unselects every card, instead of reverting to the placeholder.
    const hasSelectedVocab = Object.keys(params.vocabularyDrawerContent).length > 0;
    if (!hasSelectedVocab) {
        return "";
    }
    return _shortenWithEllipsis(params.selectedUserSubject?.name ?? "", MAX_NAME_LENGTH);
};
